// Account domain types (contract 07 §5). Ported from v3 `models/account.types.ts`; the id `Set`s
// + sysadmin literals that used to live here left for a shared access-config file and were then
// DELETED outright with it (ORP-18806) — authority is the bootstrap `rbac` section, via `useRbac()`.
// `PermissionValue` gains `string[]` for scope-lists (e.g. `export_hub_platforms`) — it mirrors
// `rbac.permissions`, a value-MAP, so it must never be narrowed to a key list.
// `normalizePackageId` ports straight.

export type AccountItem = {
  id: number
  name?: string
  user_id?: number
  workspace_count?: number
  /**
   * This switcher ROW's own MCP-born bit (ORP-21834 / ORP-21835 item 1), published on every
   * accessible-accounts row by `common/generic/views.py` — both row builders, so a sysadmin's
   * all-accounts list and an ordinary user's membership list agree.
   *
   * Same server-owned value as `AccountDetail.is_mcp_only` and subject to the same prohibition
   * (ORP-21656 AC 2): the FE must never re-derive it from `package`, because the base-package →
   * product mapping is backend-owned. The two differ only in WHEN they arrive — this one rides
   * the account LIST, so the switcher can mark a row BEFORE the user selects it, whereas
   * `account_detail` only answers after that account has been booted.
   *
   * Optional, and absent-means-web wherever it is read: an older backend, or a deploy whose
   * per-user response cache is still warm, simply yields an unmarked row. Declared rather than
   * left to the index signature below so a `boolean | undefined` read is compile-checked.
   */
  is_mcp_only?: boolean
  [k: string]: unknown
}

/** Authenticated/account-owner identity as published by bootstrap. Naming it keeps Profile's
 *  read model on module 07's typed public surface instead of probing the response index signature. */
export type AccountUserIdentity = {
  id?: number
  email?: string
  name?: string
  is_google?: boolean
  is_shopify?: boolean
  [k: string]: unknown
}

export type UserResponse = {
  user?: AccountUserIdentity
  id?: number
  email?: string
  has_password?: boolean
  [k: string]: unknown
}

export type PermissionKey = string

/** `string[]` = scope-lists (e.g. `export_hub_platforms`); the rest are flags/limits. */
export type PermissionValue = boolean | number | string | string[] | null | undefined
export type PermissionMap = Record<PermissionKey, PermissionValue>

export type AccountWorkspacePermission = {
  workspace_id?: number | string
  permissions?: PermissionMap
  package?: number[] | number | null
  [k: string]: unknown
}

export type AccountDetail = {
  user?: AccountUserIdentity
  package?: number | number[] | null
  permissions?: PermissionMap
  workspace_permissions?: AccountWorkspacePermission[]
  connected_user_count?: number
  export_job_count?: number
  /** Account avatar — a browser-ready URL or the legacy bucket object key. Declared rather than
   *  left to the index signature so `resolveProfilePictureUrl` and `<img :src>` bind under
   *  `strict` instead of receiving `unknown` (module 22's Account pane, ORP-18890). */
  profile_picture_path?: string | null
  /** Account-wide email-verification MFA. Same reason: it drives a `Switch` v-model. */
  is_mfa_enabled?: boolean
  /**
   * Server-decided: this account bought the MCP-only product, not the web one (ORP-21656 AC 2,
   * backend `accounts/bootstrap_views.py`). Declared rather than left to the index signature
   * because the router branches on it — and because the FE must never re-derive it from
   * `package`: the base-package → product mapping is backend-owned, and mirroring it here is
   * exactly the drift AC 2 forbids.
   */
  is_mcp_only?: boolean
  [k: string]: unknown
}

export type UserAccessAccount = {
  id: (number | string)[]
  level?: unknown
  [k: string]: unknown
}

export type UserAccessInfo = {
  accounts?: UserAccessAccount[]
  workspaces?: unknown[]
  dashboards?: unknown[]
  [k: string]: unknown
}

export type AccountState = {
  accounts: AccountItem[]
  account: AccountItem | null
  user: UserResponse | null
  accountDetail: AccountDetail | null
  permissions: PermissionMap
  userAccessInfo: UserAccessInfo
  packageId: number | null
  oldSelectedAccount: Record<string, number>
}

/** Accounts allowed to open the Meta App Review create-page-ad tool (ORP-20886). */
export const CREATE_META_PAGE_AD_ACCOUNT_IDS = new Set<number>([10000663, 10005480])

export const FREE_TRIAL_PACKAGE_ID = 10000127
export const FREE_PLAN_PACKAGE_ID = 10000126
export const FREE_PLAN_PACKAGE_IDS = new Set<number>([FREE_PLAN_PACKAGE_ID])
export const FREE_ACCESS_PACKAGE_IDS = new Set<number>([
  FREE_TRIAL_PACKAGE_ID,
  ...FREE_PLAN_PACKAGE_IDS,
])

export const normalizePackageId = (raw: unknown): number | null => {
  if (raw == null) return null
  if (Array.isArray(raw)) {
    const firstValue = Number(raw[0] ?? null)
    return Number.isFinite(firstValue) ? firstValue : null
  }
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null

  const normalized = Number(raw)
  return Number.isFinite(normalized) ? normalized : null
}
